import React, { useContext, useEffect, useRef, useState } from 'react'
import { FaMapMarkerAlt } from '@react-icons/all-files/fa/FaMapMarkerAlt'
import { useDispatch, useSelector } from 'react-redux'

import MapComponent from 'components/common/maps/MapComponent'
import { LanguageContext } from 'context/LanguageContext'
import { haversineDistance } from 'utils/appUtils'
import Toast from '../toast'
import { setClearCartMessage } from 'modules/location/actions'

const StoreMap = ({
    currentAddress,
    selectedStore,
    setSelectedStore,
    prevForm,
    storeNearUser,
    enteredStore,
}) => {
    const { strings } = useContext(LanguageContext)

    const pickupStores = useSelector(state => state.location.getIn(['pickupStores']))
    const currentAddressDetails = useSelector(state => state.location.getIn(['selectedAddress']))
    const hasPlatterItems = useSelector(state => state.basket.get('hasPlatterItems'))
    const isCateringMode = useSelector(state => state.appSettings.get('isCateringMode')) || hasPlatterItems
    const cateringData = useSelector(state => state.catering)
    
    // Use catering address when in catering mode
    const currentAddressInRedux = isCateringMode 
        ? cateringData?.address 
        : currentAddressDetails?.toJS()
    const enteredCoordinates = enteredStore
        ? [enteredStore.lattitude, enteredStore.longitude]
        : [currentAddressInRedux?.lattitude, currentAddressInRedux?.longitude]
    const pickupStoresNearby = pickupStores.map(store => ({
        ...store,
        distance: haversineDistance([store.lat, store.lon], enteredCoordinates),
    }))
    pickupStoresNearby.sort((a, b) => a.distance - b.distance)
    const [sortedPickupStores, setSortedPickupStores] = useState(pickupStoresNearby)
    const [selectedItem, setSelectedItem] = useState(null)
    const [showBanner, setShowBanner] = useState(null)
    const listRef = useRef(null)

    const dispatch = useDispatch()

    const showStoreDetails = () => {
        if (selectedStore) {
            return (
                <>
                    <div>
                        <FaMapMarkerAlt className={'store-content-info-icon'} color={'#008060'} />
                    </div>
                    <div className={'store-content-info-details'}>
                        <span className={'store-number'}>{`Store ${' '}${
                            selectedStore.store_id
                        }`}</span>
                        <br />
                        <span className={'store-address'}>
                            {selectedStore.address}
                            <br />
                            {selectedStore.city}
                            {', '}
                            {selectedStore.state}
                        </span>
                        <span className={'store-change'}>
                            <p onClick={prevForm}>{strings.changeLocationButton}</p>
                        </span>
                    </div>
                </>
            )
        }
        if (currentAddressInRedux && currentAddressInRedux.type === 'pickup') {
            return (
                <>
                    <div>
                        <FaMapMarkerAlt className={'store-content-info-icon'} color={'#008060'} />
                    </div>
                    <div className={'store-content-info-details'}>
                        <span className={'store-number'}>{`Store ${' '}${
                            currentAddressInRedux.store_id || ''
                        }`}</span>
                        <br />
                        <span className={'store-address'}>
                            {currentAddressInRedux.formatted_address}
                            <br />
                            {currentAddressInRedux.city}
                            {', '}
                            {currentAddressInRedux.state}
                        </span>
                        <span className={'store-change'}>
                            <p onClick={prevForm}>{strings.changeLocationButton}</p>
                        </span>
                    </div>
                </>
            )
        }
        return null
    }

    const updateSelectedStore = store => {
        const clickedItem = pickupStoresNearby.find(stores => stores?.store_id === store?.store_id)
        const otherItems = pickupStoresNearby.filter(stores => stores?.store_id !== store?.store_id)
        setSortedPickupStores([clickedItem, ...otherItems])
    }
    const onStoreSelected = store => {
        setSelectedItem(store)
        setSelectedStore(store)
        updateSelectedStore(store)
    }

    const onSelectMarker = store => {
        setSelectedStore(store)
        setSelectedItem(store)
        updateSelectedStore(store)
    }

    const renderNoStoresData = () => {
        dispatch(setClearCartMessage(true))
        return (
            <div className={'store-content noData'}>
                <h4 className={'store-content-title'}>{strings.noPickupStoresError}</h4>
                <p className={'store-change'} onClick={prevForm}>
                    {strings.tryAnotherLocationButton}
                </p>
            </div>
        )
    }

    const getContentToShow = () => {
        if (currentAddressInRedux) {
            if (
                currentAddressInRedux.formatted_address !== currentAddress?.formatted_address &&
                !storeNearUser
            ) {
                return renderNoStoresData()
            } else if (!storeNearUser) {
                return <div className={'store-content'}>{getStoreDetails()}</div>
            } else {
                return <div className={'store-content'}>{getStoreDetails()}</div>
            }
        } else if (!storeNearUser) {
            return renderNoStoresData()
        } else {
            return <div className={'store-content'}>{getStoreDetails()}</div>
        }
    }

    const getStoreDetails = () => {
        if (sortedPickupStores.length && selectedItem) {
            return (
                <ul ref={listRef} className="store-content-list">
                    {sortedPickupStores.map(store => (
                        <>
                            <li
                                className="store-content-list-items"
                                onClick={() => onStoreSelected(store)}
                            >
                                <div
                                    className={`store-content-list-items-item ${selectedItem?.address === store?.address ? 'selected' : ''}`}
                                >
                                    <span
                                        className={`store-number ${selectedItem?.address === store?.address ? 'selected' : ''}`}
                                    >{`${store?.name}`}</span>
                                    <br />
                                    <span
                                        className={`store-address ${selectedItem?.address === store?.address ? 'selected' : ''}`}
                                    >
                                        {store?.address} {store?.city}
                                        {', '}
                                        {store?.state}
                                    </span>
                                    <br />
                                </div>
                            </li>
                        </>
                    ))}
                </ul>
            )
        } else if (sortedPickupStores.length && currentAddressInRedux) {
            return (
                <ul ref={listRef} className="store-content-list">
                    {sortedPickupStores.map(store => (
                        <>
                            <li
                                className="store-content-list-items"
                                onClick={() => onStoreSelected(store)}
                            >
                                <div
                                    className={`store-content-list-items-item ${currentAddressInRedux.formatted_address === store?.address ? 'selected' : ''}`}
                                >
                                    <span
                                        className={`store-number ${currentAddressInRedux.formatted_address === store?.address ? 'selected' : ''}`}
                                    >{`${store?.name}`}</span>
                                    <br />
                                    <span
                                        className={`store-address ${currentAddressInRedux.formatted_address === store?.address ? 'selected' : ''}`}
                                    >
                                        {store?.address} {store?.city}
                                        {', '}
                                        {store?.state}
                                    </span>
                                    <br />
                                </div>
                            </li>
                        </>
                    ))}
                </ul>
            )
        } else if (sortedPickupStores.length) {
            return (
                <ul ref={listRef} className="store-content-list">
                    {sortedPickupStores.map(store => (
                        <>
                            <li
                                className="store-content-list-items"
                                onClick={() => onStoreSelected(store)}
                            >
                                <div className={`store-content-list-items-item`}>
                                    <span className={`store-number`}>{`${store?.name}`}</span>
                                    <br />
                                    <span className={`store-address`}>
                                        {store?.address} {store?.city}
                                        {', '}
                                        {store?.state}
                                    </span>
                                    <br />
                                </div>
                            </li>
                        </>
                    ))}
                </ul>
            )
        }
    }
    useEffect(() => {
        setShowBanner(true)
        setTimeout(() => {
            setShowBanner(false)
        }, 5000)
    }, [])
    useEffect(() => {
        setSortedPickupStores(pickupStoresNearby)
        if (enteredStore) {
            const selectStore = pickupStoresNearby.find(stores => stores.zip === enteredStore.zip)
            setSelectedStore(selectStore)
            setSelectedItem(selectStore)
        }
    }, [enteredStore])
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo(0, 0)
        }
    }, [sortedPickupStores])
    return (
        <>
            <Toast
                message="Hey!! We found pickup stores near you"
                showBanner={
                    showBanner &&
                    (storeNearUser ||
                        currentAddressInRedux?.formatted_address ===
                            currentAddress?.formatted_address)
                }
            />
            <div className={'store'}>
                {(storeNearUser ||
                    currentAddressInRedux?.formatted_address ===
                        currentAddress?.formatted_address) && (
                    <div className={'store-map'}>
                        <MapComponent
                            stores={pickupStores}
                            selectedAddress={currentAddress}
                            setSelectedStore={store => onSelectMarker(store)}
                            selectedStore={selectedStore}
                            selectedItem={selectedItem}
                        />
                    </div>
                )}
                {getContentToShow()}
            </div>
        </>
    )
}

export default StoreMap
